/**
 * Data layer for the MTTR snapshot.
 *
 * Reads /api/boxes on the portal API (all users, every role). The backend
 * projects the response per-role: platform gets the full operator payload,
 * customer/vendor get a trimmed field set. Scoping happens server-side via
 * vw_user_scu_access -- this never carries data the user can't see.
 */

import { ApiError, apiFetch } from "../services/apiClient";
import { withRecomputedCounts } from "../lib/stateCounts";
import { configService } from "../services/configService";
import type { BoxesSnapshot } from "../types/boxes";
import type { RecoverySnapshot } from "../types/recovery";
import type { UptimeSnapshot } from "../types/uptime";

export class MttrApiError extends Error {
  constructor(
    message: string,
    readonly status?: number,
  ) {
    super(message);
    this.name = "MttrApiError";
  }
}

/** Shared JSON GET against the portal API; rethrows the shared ApiError as
 *  MttrApiError so existing call sites / constructors keep working. */
async function getJson<T>(path: string, token: string): Promise<T> {
  try {
    return await apiFetch<T>(path, token);
  } catch (e) {
    throw e instanceof ApiError ? new MttrApiError(e.message, e.status) : e;
  }
}

/**
 * Fetch the current fleet snapshot from the portal API. Requires an auth
 * token. `state_counts` / `total_boxes` / `stacked_count` are re-derived
 * client-side via `withRecomputedCounts`.
 */
export async function fetchBoxes(token: string): Promise<BoxesSnapshot> {
  const data = await getJson<{
    generated_utc: string;
    boxes: BoxesSnapshot["boxes"];
    mqtt_publish_recent_minutes?: number;
  }>("/api/boxes", token);
  if (!Array.isArray(data?.boxes)) {
    throw new MttrApiError("/api/boxes: malformed payload");
  }
  // Stacked boxes get pulled out of their underlying state, matching
  // every other snapshot path (see lib/stateCounts).
  return withRecomputedCounts({
    generated_utc: data.generated_utc,
    boxes: data.boxes,
    mqtt_publish_recent_minutes: data.mqtt_publish_recent_minutes,
  } as BoxesSnapshot);
}

/**
 * Fetch the SCU names the current user may see (GET /api/me/scus) — the
 * authoritative visible set from vw_user_scu_access. Box dropdowns/searches
 * filter to this so they reflect the user's permissions/role/customer scope
 * regardless of which snapshot source the role uses.
 */
export async function fetchVisibleScus(token: string): Promise<string[]> {
  const data = await getJson<{ scus?: string[] }>("/api/me/scus", token);
  return Array.isArray(data.scus) ? data.scus : [];
}

/** Dashboard fleet-wide rollup: total inventory + refill counts, scoped
 *  to the user's visible smartboxes server-side via vw_user_scu_access. */
export interface DashboardTotals {
  total_inventory: number;
  refill_lines: number;
  refill_boxes: number;
  /** Month-to-date count of departure tickets where status was
   *  CONFIRMED or PARTIAL — i.e. real takes from the fleet's
   *  cameras. VOID tickets excluded (no inventory moved). */
  mtd_departures: number;
  /** Month-to-date priced value of CONFIRMED EPC lines. Null when
   *  no priced lines exist (vs 0 — keeps "unknown" distinct from
   *  "we know it's zero"). When non-null and `mtd_unpriced_lines`
   *  is also non-zero, the value is a lower bound. */
  mtd_value: number | null;
  mtd_value_currency: string | null;
  mtd_unpriced_lines: number;
  /** Month-to-date count of ARRIVAL events (NEW_ARRIVAL +
   *  RE_ARRIVAL) in the user's scope — what the Arrivals page
   *  totals show, surfaced as a dashboard tile. */
  mtd_arrivals: number;
}

export async function fetchDashboardTotals(
  token: string,
): Promise<DashboardTotals> {
  const d = await getJson<Partial<DashboardTotals>>("/api/dashboard", token);
  return {
    total_inventory: Number(d.total_inventory ?? 0),
    refill_lines: Number(d.refill_lines ?? 0),
    refill_boxes: Number(d.refill_boxes ?? 0),
    mtd_departures: Number(d.mtd_departures ?? 0),
    mtd_value: d.mtd_value == null ? null : Number(d.mtd_value),
    mtd_value_currency: d.mtd_value_currency ?? null,
    mtd_unpriced_lines: Number(d.mtd_unpriced_lines ?? 0),
    mtd_arrivals: Number(d.mtd_arrivals ?? 0),
  };
}

/** Two-bucket tag reliability rollup for the Dashboard tile, over the
 *  tags CURRENTLY ON-HAND in the caller's smartboxes (latest scan's
 *  EPCs). Buckets collapse from `v2_tag_reliability_snapshot`:
 *    Healthy  = HEALTHY + MOSTLY_READ
 *    Abnormal = FLICKERY + UNRELIABLE
 *  On-hand tags without a reliability snapshot yet are excluded.
 *  Scoped server-side to the caller's visible SCUs.
 *  `window_days` echoes back the query filter (null = all-time). */
export interface TagHealthTotals {
  healthy: number;
  abnormal: number;
  total: number;
  window_days: number | null;
}

export async function fetchTagHealth(
  token: string, windowDays: number | null = null,
): Promise<TagHealthTotals> {
  const qs = windowDays == null ? "" : `?days=${encodeURIComponent(windowDays)}`;
  const d = await getJson<Partial<TagHealthTotals>>(
    `/api/dashboard/tag-health${qs}`,
    token,
  );
  return {
    healthy: Number(d.healthy ?? 0),
    abnormal: Number(d.abnormal ?? 0),
    total: Number(d.total ?? 0),
    window_days: d.window_days == null ? null : Number(d.window_days),
  };
}

/** Fetch the current uptime snapshot. Same atomic-write semantics as
 *  boxes.json; cache-busted with a timestamp param. */
export async function fetchUptime(): Promise<UptimeSnapshot> {
  const cfg = configService.getConfig();
  const path = cfg.mttr.uptimeJsonPath ?? "/uptime.json";
  const url = `${path}?t=${Date.now()}`;
  const response = await fetch(url, {
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!response.ok) {
    throw new MttrApiError(
      `uptime.json fetch failed: ${response.status} ${response.statusText}`,
      response.status,
    );
  }
  const data = (await response.json()) as UptimeSnapshot;
  if (
    typeof data?.generated_utc !== "string" ||
    typeof data?.windows !== "object" ||
    data.windows === null
  ) {
    throw new MttrApiError("uptime.json: malformed payload (missing fields)");
  }
  return data;
}

/** Fetch the current recovery snapshot. Same atomic-write pattern as
 *  boxes.json / uptime.json. Path is `/recovery.json` (or whatever
 *  `recoveryJsonPath` points at in runtime config — falls back to
 *  the standard path if unset). */
export async function fetchRecovery(): Promise<RecoverySnapshot> {
  const cfg = configService.getConfig();
  const path = cfg.mttr.recoveryJsonPath ?? "/recovery.json";
  const url = `${path}?t=${Date.now()}`;
  const response = await fetch(url, {
    headers: { Accept: "application/json" },
    cache: "no-store",
  });
  if (!response.ok) {
    throw new MttrApiError(
      `recovery.json fetch failed: ${response.status} ${response.statusText}`,
      response.status,
    );
  }
  const data = (await response.json()) as RecoverySnapshot;
  if (
    typeof data?.generated_utc !== "string" ||
    typeof data?.windows !== "object" ||
    data.windows === null
  ) {
    throw new MttrApiError("recovery.json: malformed payload (missing fields)");
  }
  return data;
}
